<template>
  <div class="priority-codes-container">
    <div class="card">
      <div class="card-header d-flex justify-content-between align-items-center">
        <h4 class="card-title mb-0">Priority Codes Management</h4>
        <button class="btn btn-primary" @click="showAddModal = true">
          <i class="fas fa-plus me-1"></i> Add New Code
        </button>
      </div>
      <div class="card-body">
        <!-- Search and Filter -->
        <div class="row mb-3">
          <div class="col-md-6">
            <div class="input-group">
              <input 
                type="text" 
                class="form-control" 
                placeholder="Search codes..." 
                v-model="searchQuery"
              >
              <button class="btn btn-outline-secondary" type="button">
                <i class="fas fa-search"></i>
              </button>
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex justify-content-end">
              <div class="btn-group">
                <button 
                  class="btn" 
                  :class="filterStatus === 'all' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="filterStatus = 'all'"
                >
                  All
                </button>
                <button 
                  class="btn" 
                  :class="filterStatus === 'active' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="filterStatus = 'active'"
                >
                  Active
                </button>
                <button 
                  class="btn" 
                  :class="filterStatus === 'inactive' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="filterStatus = 'inactive'"
                >
                  Inactive
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">Loading priority codes...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger">
          {{ error }}
          <button class="btn btn-link" @click="loadPriorityCodes">Try Again</button>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredCodes.length === 0" class="text-center py-5">
          <i class="fas fa-key fa-3x text-muted mb-3"></i>
          <p class="lead">No priority codes found</p>
          <button class="btn btn-primary" @click="showAddModal = true">
            Add Your First Code
          </button>
        </div>

        <!-- Codes Table -->
        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead class="table-light">
              <tr>
                <th>Code</th>
                <th>Description</th>
                <th>Assigned To</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="code in filteredCodes" :key="code.id">
                <td>
                  <span class="badge bg-primary">{{ code.code }}</span>
                </td>
                <td>{{ code.description }}</td>
                <td>
                  <span v-if="code.assigned_to" class="badge bg-info">
                    {{ code.assigned_to }}
                  </span>
                  <span v-else class="text-muted">Not assigned</span>
                </td>
                <td>
                  <span :class="code.is_active ? 'badge bg-success' : 'badge bg-danger'">
                    {{ code.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>{{ formatDate(code.created_at) }}</td>
                <td>
                  <div class="btn-group">
                    <button 
                      class="btn btn-sm btn-outline-primary" 
                      @click="editCode(code)"
                      title="Edit"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      class="btn btn-sm btn-outline-danger" 
                      @click="confirmDelete(code)"
                      title="Delete"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div class="modal fade" :class="{ 'show d-block': showAddModal || showEditModal }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ showEditModal ? 'Edit Priority Code' : 'Add New Priority Code' }}
            </h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="showEditModal ? updateCode() : createCode()">
              <div class="form-group mb-3">
                <label for="code" class="form-label">Code:</label>
                <input 
                  type="text" 
                  id="code" 
                  v-model="currentCode.code" 
                  class="form-control"
                  required
                  maxlength="4"
                  pattern="[A-Z0-9]{4}"
                  title="4 characters (uppercase letters and numbers only)"
                >
                <small class="form-text text-muted">
                  4 characters (uppercase letters and numbers only)
                </small>
              </div>
              
              <div class="form-group mb-3">
                <label for="description" class="form-label">Description:</label>
                <textarea 
                  id="description" 
                  v-model="currentCode.description" 
                  class="form-control"
                  rows="3"
                  required
                ></textarea>
              </div>

              <div class="form-group mb-3">
                <label for="assigned_to" class="form-label">Assigned To:</label>
                <input 
                  type="text" 
                  id="assigned_to" 
                  v-model="currentCode.assigned_to" 
                  class="form-control"
                  placeholder="Enter personnel name"
                >
                <small class="form-text text-muted">
                  Optional: Enter the name of the personnel assigned to this code
                </small>
              </div>
              
              <div class="form-group mb-3">
                <div class="form-check">
                  <input 
                    type="checkbox" 
                    id="is_active" 
                    v-model="currentCode.is_active" 
                    class="form-check-input"
                  >
                  <label class="form-check-label" for="is_active">
                    Active
                  </label>
                </div>
              </div>
              
              <div class="text-end">
                <button type="button" class="btn btn-secondary me-2" @click="closeModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="submitting">
                  <i class="fas fa-spinner fa-spin me-1" v-if="submitting"></i>
                  {{ showEditModal ? 'Update' : 'Create' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div class="modal fade" :class="{ 'show d-block': showDeleteModal }" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirm Delete</h5>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete this priority code?</p>
            <p class="text-danger">
              <strong>Code:</strong> {{ codeToDelete?.code }}<br>
              <strong>Description:</strong> {{ codeToDelete?.description }}
            </p>
            <p class="text-warning">
              <i class="fas fa-exclamation-triangle me-1"></i>
              This action cannot be undone.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">
              Cancel
            </button>
            <button 
              type="button" 
              class="btn btn-danger" 
              @click="deleteCode"
              :disabled="deleting"
            >
              <i class="fas fa-spinner fa-spin me-1" v-if="deleting"></i>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { priorityCodesApi } from '@/services/api'

export default {
  name: 'PriorityCodes',
  data() {
    return {
      loading: true,
      submitting: false,
      deleting: false,
      error: null,
      codes: [],
      searchQuery: '',
      filterStatus: 'all',
      showAddModal: false,
      showEditModal: false,
      showDeleteModal: false,
      currentCode: {
        code: '',
        description: '',
        is_active: true,
        assigned_to: ''
      },
      codeToDelete: null
    }
  },
  computed: {
    filteredCodes() {
      let filtered = this.codes

      // Apply status filter
      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(code => 
          this.filterStatus === 'active' ? code.is_active : !code.is_active
        )
      }

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(code => 
          code.code.toLowerCase().includes(query) ||
          code.description.toLowerCase().includes(query) ||
          (code.assigned_to || '').toLowerCase().includes(query)
        )
      }

      return filtered
    }
  },
  created() {
    this.loadPriorityCodes()
  },
  methods: {
    async loadPriorityCodes() {
      this.loading = true
      this.error = null
      try {
        const response = await priorityCodesApi.getPriorityCodes()
        this.codes = response.data
      } catch (error) {
        console.error('Error loading priority codes:', error)
        this.error = 'Failed to load priority codes. Please try again.'
      } finally {
        this.loading = false
      }
    },
    formatDate(date) {
      return format(new Date(date), 'MMM d, yyyy h:mm a')
    },
    editCode(code) {
      this.currentCode = { ...code }
      this.showEditModal = true
    },
    confirmDelete(code) {
      this.codeToDelete = code
      this.showDeleteModal = true
    },
    closeModal() {
      this.showAddModal = false
      this.showEditModal = false
      this.currentCode = {
        code: '',
        description: '',
        is_active: true,
        assigned_to: ''
      }
    },
    async createCode() {
      this.submitting = true
      try {
        // Ensure code is uppercase and handle empty values
        const data = {
          ...this.currentCode,
          code: this.currentCode.code.toUpperCase(),
          is_active: Boolean(this.currentCode.is_active),
          assigned_to: this.currentCode.assigned_to || null
        }
        
        console.log('Sending data:', data) // Debug log
        
        const response = await priorityCodesApi.createPriorityCode(data)
        console.log('Response:', response) // Debug log
        
        this.closeModal()
        this.loadPriorityCodes()
        this.$emit('success', 'Priority code created successfully')
      } catch (error) {
        console.error('Error creating priority code:', error)
        console.error('Error response:', error.response) // Debug log
        
        let errorMessage = 'Failed to create priority code'
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        }
        
        this.$emit('error', errorMessage)
      } finally {
        this.submitting = false
      }
    },
    async updateCode() {
      this.submitting = true
      try {
        // Ensure code is uppercase and handle empty values
        const data = {
          ...this.currentCode,
          code: this.currentCode.code.toUpperCase(),
          is_active: Boolean(this.currentCode.is_active),
          assigned_to: this.currentCode.assigned_to || null
        }
        
        await priorityCodesApi.updatePriorityCode(this.currentCode.id, data)
        this.closeModal()
        this.loadPriorityCodes()
        this.$emit('success', 'Priority code updated successfully')
      } catch (error) {
        console.error('Error updating priority code:', error)
        const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to update priority code'
        this.$emit('error', errorMessage)
      } finally {
        this.submitting = false
      }
    },
    async deleteCode() {
      this.deleting = true
      try {
        await priorityCodesApi.deletePriorityCode(this.codeToDelete.id)
        this.showDeleteModal = false
        this.loadPriorityCodes()
        this.$emit('success', 'Priority code deleted successfully')
      } catch (error) {
        console.error('Error deleting priority code:', error)
        this.$emit('error', error.response?.data?.message || 'Failed to delete priority code')
      } finally {
        this.deleting = false
      }
    }
  }
}
</script>

<style scoped>
.priority-codes-container {
  max-width: 1200px;
  margin: 0 auto;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal.show {
  display: block;
}

.modal-dialog {
  margin: 1.75rem auto;
}

.badge {
  font-size: 0.875rem;
  padding: 0.5em 0.75em;
}

/* Portrait mode adjustments */
@media screen and (orientation: portrait) {
  .modal-dialog {
    width: 95%;
    margin: 1rem auto;
  }
  
  .table {
    font-size: 1.1rem;
  }
  
  .badge {
    font-size: 1rem;
  }
  
  .btn-group .btn {
    padding: 0.5rem 0.75rem;
  }
}
</style>
